import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Divider,
  CircularProgress,
  Chip,
} from "@mui/material";
import { Download as DownloadIcon } from "@mui/icons-material";
import axios from "axios";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import s3Utils from '../../../utils/s3Utils';
import { decryptAppId, decryptText } from '../../../utils/encryption';
import previewReportService from '../../../services/previewReportService';


const Form73Report = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [labDetailsState, setlabDetailsState] = useState([]);
  const [disciplineState, setDiscipline] = useState([]);
  const [locationDetailsState, setLocationDetails] = useState([]);
  const [scopeState, setScope] = useState([]);
  const [organisationDetailsState, setOrganisationDetails] = useState([]);
  const [equipmentDetailsState, setEquipmentDetails] = useState([]);
  const [referenceMaterialsState, setReferenceMaterials] = useState([]);
  const [observationsState, setObservations] = useState([]);
  const [nonAnalyticalInstrumentsState, setNonAnalyticalInstruments] = useState([]);
  const [form153PhantomsState, setForm153Phantoms] = useState([]);
  const [radioDetailsState, setRadioDetails] = useState([]);
  const [proficiencyTestingState, setProficiencyTesting] = useState([]);
  const [organizationObservationsState, setOrganizationObservations] = useState([]);
  const [downloading, setDownloading] = useState(false);
  const [isObservationVisible, setIsObservationVisible] = useState(false);
  const [isAutoDownloading, setIsAutoDownloading] = useState(false);

  useEffect(() => {
    const encryptedAppId = getQueryParam("appId");
    const encryptedOrgId = getQueryParam("orgId");
    const encryptedAssessmentId = getQueryParam("assessmentId");
    const encryptedObservation = getQueryParam("observation");

    console.log('🔍 PreviewReport - Encrypted parameters received:', {
      encryptedAppId,
      encryptedOrgId,
      encryptedAssessmentId,
      encryptedObservation
    });

    if (encryptedAppId && encryptedOrgId && encryptedAssessmentId && encryptedObservation) {
      try {
        // Decrypt the parameters
        const decryptedAppId = decryptAppId(encryptedAppId);
        const decryptedOrgId = decryptText(encryptedOrgId);
        const decryptedAssessmentId = decryptText(encryptedAssessmentId);
        const decryptedObservation = decryptText(encryptedObservation);

        console.log('🔓 PreviewReport - Decrypted parameters:', {
          appId: decryptedAppId,
          orgId: decryptedOrgId,
          observation: decryptedObservation
        });

        // Use decrypted parameters
        if (decryptedAppId && decryptedOrgId && decryptedAssessmentId) {
          fetchForm153Data(decryptedAppId, decryptedOrgId, decryptedAssessmentId);
        }
        setIsObservationVisible(decryptedObservation === "true");

        console.log('✅ PreviewReport - Parameters successfully decrypted and used');
      } catch (error) {
        console.error('❌ Error decrypting preview report parameters:', error);
        // Fallback to original behavior if decryption fails
        const appId = encryptedAppId;
        const orgId = encryptedOrgId;
        const assessmentId = encryptedAssessmentId;
        const observation = encryptedObservation;
        if (appId && orgId && assessmentId) {
          fetchForm153Data(appId, orgId, assessmentId);
        }
        setIsObservationVisible(observation === "true");
      }
    }
  }, [location.search]);

  // Handle auto-download functionality
  useEffect(() => {
    const autoDownload = getQueryParam("autoDownload");
    const fromSubmission = getQueryParam("fromSubmission");

    if (autoDownload === "true" && fromSubmission === "true" && !isAutoDownloading && labDetailsState.length > 0) {
      console.log('🚀 Auto-downloading PDF after form submission...');
      setIsAutoDownloading(true);

      // Small delay to ensure all data is loaded
      setTimeout(() => {
        handleAutoDownloadAndSave();
      }, 1000);
    }
  }, [labDetailsState, isAutoDownloading]);

  const getQueryParam = (param) => {
    const params = new URLSearchParams(location.search);
    return params.get(param);
  };

  const fetchForm153Data = async (appId, orgId, assessmentId) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/preview-report/form73/${appId}/${orgId}/${assessmentId}`
      );

      if (response.data.success) {
        const {
          labDetails,
          discipline,
          locationDetails,
          scope,
          organisationDetails,
          equipmentDetails,
          referenceMaterials,
          observations,
          nonAnalyticalInstruments,
          form153Phantoms,
          form153KitsRadioisotopes,
          proficiencyTestingProviders,
          organizationObservations
        } = response.data.data;

        setlabDetailsState(labDetails);
        setDiscipline(discipline);
        setLocationDetails(locationDetails);
        setScope(scope);
        setOrganisationDetails(organisationDetails.filter(item => item.IsAuthorisedSignatory === true));
        setEquipmentDetails(equipmentDetails);
        setReferenceMaterials(referenceMaterials);
        setObservations(observations);
        setNonAnalyticalInstruments(nonAnalyticalInstruments);
        setForm153Phantoms(form153Phantoms);
        setRadioDetails(form153KitsRadioisotopes);
        setProficiencyTesting(proficiencyTestingProviders);
        setOrganizationObservations(organizationObservations || []);
      } else {
        console.error("API Error:", response.data.message);
      }
    } catch (error) {
      console.error("Request Failed:", error.message);
    }
  };

  const getObservationBySection = (sectionName, sourceId) => {
    if (sourceId) {
      return observationsState.find(
        (item) =>
          item.SectionName &&
          item.SectionName.toLowerCase() === sectionName.toLowerCase() &&
          item.sourceid === sourceId
      );
    } else {
      return observationsState.find(
        (item) =>
          item.SectionName &&
          item.SectionName.toLowerCase() === sectionName.toLowerCase()
      );
    }
  };

  const getObservationBySubSection = (sectionName, subSectionName = "") => {
    return observationsState.find(
      (item) =>
        item.SectionName?.toLowerCase() === sectionName.toLowerCase() &&
        (subSectionName
          ? item.SubsectionName?.toLowerCase() === subSectionName.toLowerCase()
          : true)
    );
  };

  // Get observation text from the Observation field
  const getObservationText = (observationData) => {
    if (!observationData) return "";
    return observationData.Observation || "";
  };

  // Get authorized areas for employee based on organizationObservations
  const getAuthorizedAreas = (empId) => {
    const authorizedAreas = organizationObservationsState.filter(obs => obs.emp_id === empId);
    
    if (authorizedAreas.length === 0) {
      return (
        <Typography variant="body2" sx={{ color: '#64748b', fontStyle: 'italic' }}>
          No specific authorization found
        </Typography>
      );
    }

    return (
      <TableContainer 
        sx={{ 
          maxHeight: '200px',
          '& .MuiTable-root': {
            minWidth: 'auto'
          }
        }}
      >
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', p: 1 }}>S No.</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', p: 1 }}>Assessed by</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', p: 1 }}>Assessed Date</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', p: 1 }}>Discipline</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', p: 1 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', p: 1 }}>Remarks</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {authorizedAreas.map((area, index) => {
              // Handle discipline names and remove duplicates
              let disciplineName;
              if (Array.isArray(area.disciplineName)) {
                // Remove duplicates using Set and join with comma
                const uniqueDisciplines = [...new Set(area.disciplineName)];
                disciplineName = uniqueDisciplines.join(', ');
              } else {
                disciplineName = area.disciplineName || 'N/A';
              }
              
              const assessorName = Array.isArray(area.Name) 
                ? area.Name[0] 
                : area.Name || 'N/A';

              const assessedDate = area.insertdate 
                ? formatDate(area.insertdate)
                : (area.InsertDate ? formatDate(area.InsertDate) : 'N/A');

              const status = area.Status || 'N/A';
              const remarks = area.Remarks || 'satisfactory';

              return (
                <TableRow key={index}>
                  <TableCell sx={{ fontSize: '0.75rem', p: 1 }}>{index + 1}</TableCell>
                  <TableCell sx={{ fontSize: '0.75rem', p: 1 }}>{assessorName}</TableCell>
                  <TableCell sx={{ fontSize: '0.75rem', p: 1 }}>{assessedDate}</TableCell>
                  <TableCell sx={{ fontSize: '0.75rem', p: 1 }}>{disciplineName}</TableCell>
                  <TableCell sx={{ fontSize: '0.75rem', p: 1 }}>
                    <Chip 
                      label={status}
                      size="small"
                      variant="outlined"
                      sx={{
                        backgroundColor: status === 'Adequate' ? '#dcfce7' : '#fee2e2',
                        color: status === 'Adequate' ? '#16a34a' : '#dc2626',
                        fontWeight: 600,
                        fontSize: '0.7rem'
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.75rem', p: 1 }}>{remarks}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };


  // Render observation cell content - displays the Observation text and status
  const renderObservationCell = (observationData) => {
    if (!observationData) return "No observation";

    const observation = observationData.Observation;
    const status = observationData.InformationStatus;
    const informationProvided = observationData.InformationProvided;

    // Check if observation text is "Document adequacy check completed" and status is Adequate
    const isAdequate = observation &&
      observation.trim().toLowerCase() === "document adequacy check completed" &&
      status && status.toLowerCase() === "adequate";

    // If status is Adequate, show green "Adequate"
    if (isAdequate) {
      return (
        <div style={{ textAlign: "left" }}>
          <span style={{  fontWeight: "bold" }}>Assessed By : {observationData.Name || "Unknown"}</span><br/>
          <span style={{ color: "green", fontWeight: "bold" }}>Adequate</span>
        </div>
      );
    }

    // If status is Inadequate, show InformationProvided in red
    if (status && status.toLowerCase() === "inadequate") {
      return (
        <div style={{ textAlign: "left" }}>
          <span style={{  fontWeight: "bold" }}>Assessed By : {observationData.Name || "Unknown"}</span><br/>
          <span style={{ color: "red", fontWeight: "bold" }}>
            {informationProvided || observation || "No information provided"}
          </span>
        </div>
      );
    }

    // Default: show observation in black
    return (
      <div style={{ textAlign: "left" }}>
        <span style={{ color: "black" }}>
          {observation || "No observation"}
        </span>
      </div>
    );
  };


  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-GB");
    } catch (error) {
      return dateString;
    }
  };

  // Auto-download PDF and save to database after form submission
  const handleAutoDownloadAndSave = async () => {
    console.log('🎯 Starting auto PDF generation and save process...');

    try {
      const encryptedappId = getQueryParam("appId");
      const encryptedorgId = getQueryParam("orgId");

      const appId = decryptAppId(encryptedappId);
      const orgId = decryptText(encryptedorgId);

      if (!appId || !orgId) {
        console.error('Missing appId or orgId for PDF generation');
        navigate('/main/dashboard');
        return;
      }

      console.log('📄 Auto-generating PDF...');

      // Generate PDF using the existing handleDownloadPDF logic but save to database
      const now = new Date();
      const dateStr = `${now.getMonth() + 1}-${now.getDate()}-${now.getFullYear()}`;
      const hours = now.getHours().toString().padStart(2, "0");
      const minutes = now.getMinutes().toString().padStart(2, "0");
      const ampm = hours >= 12 ? "PM" : "AM";
      const hour12 = (hours % 12 || 12).toString().padStart(2, "0");
      const timeStr = `${hour12}-${minutes}${ampm}`;
      const filename = `PreviewReport_${appId}-${dateStr}_${timeStr}.pdf`;

      // Create the same PDF as the download function but get it as blob
      const reportData = {
        labDetailsState,
        disciplineState,
        scopeState,
        organisationDetailsState,
        equipmentDetailsState,
        referenceMaterialsState,
        nonAnalyticalInstrumentsState,
        observationsState,
        form153PhantomsState,
        radioDetailsState,
        proficiencyTestingState
      };

      console.log('🔧 Generating PDF with comprehensive service...');

      // Use the preview report service to generate and save PDF
      const result = await previewReportService.generateAndSavePreviewReport73(
        orgId,
        appId,
        3, // Medical lab type ID
        'PreviewReport',
        isObservationVisible
      );

      if (result.success) {
        console.log('✅ PDF generated and saved successfully!');
        console.log('📊 Result:', result);

        // Show success message and redirect to dashboard
        setTimeout(() => {
          console.log('🏠 Redirecting to dashboard...');
          navigate('/main/dashboard');
        }, 2000);
      } else {
        console.error('❌ Failed to generate and save PDF:', result.message);
        alert(`Failed to generate PDF: ${result.message}`);
        navigate('/main/dashboard');
      }

    } catch (error) {
      console.error('❌ Error in auto PDF generation and save:', error);
      alert(`Error generating PDF: ${error.message}`);
      navigate('/main/dashboard');
    }
  };



  // Get the first lab detail (assuming there's at least one)
  const labDetail = labDetailsState.length > 0 ? labDetailsState[0] : {};


  const viewS3File = async (fileName) => {
    try {
      const url = await s3Utils.generateSignedUrlForFilename(fileName);
      if (url) {
        window.open(url, '_blank', 'noopener,noreferrer');
      }
    } catch (error) {
      console.error("Error generating S3 URL:", error);
    }
  };

  return (
    <Box sx={{ p: 3, bgcolor: "#f5f5f5", minHeight: "100vh" }}>
      <Box sx={{ mb: 4 }}>
        {/* Section: Organization Staff Details */}
        <Paper 
          elevation={2} 
          sx={{ 
            mb: 3, 
            borderRadius: 2,
            overflow: 'hidden',
            border: '1px solid #e0e0e0'
          }}
        >
          {/* Enhanced Header */}
          <Box 
            sx={{ 
              background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
              color: 'white',
              p: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}
          >
            <Typography
              variant="h6"
              sx={{ 
                fontWeight: 600,
                fontSize: '1.1rem',
                letterSpacing: '0.5px'
              }}
            >
              Form 73
            </Typography>
          </Box>

          {/* Table Container */}
          <TableContainer 
            sx={{ 
              '&::-webkit-scrollbar': {
                width: '8px',
                height: '8px',
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: '#f1f1f1',
                borderRadius: '4px',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: '#c1c1c1',
                borderRadius: '4px',
                '&:hover': {
                  backgroundColor: '#a8a8a8',
                },
              },
            }}
          >
            <Table 
              stickyHeader
              sx={{
                minWidth: 1200,
                border: '1px solid #e2e8f0',
                '& .MuiTableCell-head': {
                  backgroundColor: '#f8fafc',
                  borderBottom: '2px solid #e2e8f0',
                  borderRight: '1px solid #e2e8f0',
                  fontWeight: 700,
                  fontSize: '0.875rem',
                  color: '#374151',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                },
                '& .MuiTableCell-body': {
                  fontSize: '0.875rem',
                  borderBottom: '1px solid #f1f5f9',
                  borderRight: '1px solid #e2e8f0',
                },
                '& .MuiTableRow-root': {
                  '&:hover': {
                    backgroundColor: '#f8fafc',
                    transform: 'scale(1.001)',
                    transition: 'all 0.2s ease-in-out',
                  },
                  '&:nth-of-type(even)': {
                    backgroundColor: '#fafbfc',
                  },
                },
                '& .MuiTableCell-root': {
                  border: '1px solid #e2e8f0',
                },
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell 
                    align="center" 
                    sx={{ 
                      minWidth: 60,
                      position: 'sticky',
                      left: 0,
                      backgroundColor: '#f8fafc',
                      zIndex: 1,
                      boxShadow: '2px 0 5px -2px rgba(0,0,0,0.1)'
                    }}
                  >
                    Sr No.
                  </TableCell>
                  <TableCell align="center" sx={{ minWidth: 280 }}>
                    Personal Information
                  </TableCell>
                  <TableCell align="center" sx={{ minWidth: 240 }}>
                    Contact Information
                  </TableCell>
                  <TableCell align="center" sx={{ minWidth: 280 }}>
                    Work Information
                  </TableCell>
                  <TableCell align="center" sx={{ minWidth: 300 }}>
                    Authorized for which specific area
                  </TableCell>
                  {isObservationVisible && (
                    <TableCell align="center" sx={{ minWidth: 180 }}>
                      Observation
                    </TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {organisationDetailsState.length > 0 ? (
                  organisationDetailsState.map((staff, idx) => (
                    <TableRow 
                      key={staff.Id}
                      sx={{
                        '& .MuiTableCell-root': {
                          py: 2,
                          verticalAlign: 'top',
                        }
                      }}
                    >
                      <TableCell 
                        align="center" 
                        sx={{ 
                          position: 'sticky',
                          left: 0,
                          backgroundColor: 'inherit',
                          zIndex: 1,
                          boxShadow: '2px 0 5px -2px rgba(0,0,0,0.05)',
                          fontWeight: 600,
                          color: '#1976d2',
                          borderRight: '1px solid #e2e8f0'
                        }}
                      >
                        {idx + 1}
                      </TableCell>
                      <TableCell align="left">
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
                            <Typography variant="caption" sx={{ fontWeight: 600, color: '#374151', minWidth: '70px' }}>
                              Name:
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#1f2937' }}>
                              {staff.Name || "N/A"}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
                            <Typography variant="caption" sx={{ fontWeight: 600, color: '#374151', minWidth: '70px' }}>
                              Designation:
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#1f2937' }}>
                              {staff.Designation || "N/A"}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
                            <Typography variant="caption" sx={{ fontWeight: 600, color: '#374151', minWidth: '70px' }}>
                              Qualification:
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#1f2937' }}>
                              {staff.Qualification || "N/A"}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
                            <Typography variant="caption" sx={{ fontWeight: 600, color: '#374151', minWidth: '70px' }}>
                              Specialization:
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#1f2937' }}>
                              {staff.Specialization || "N/A"}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
                            <Typography variant="caption" sx={{ fontWeight: 600, color: '#374151', minWidth: '70px' }}>
                              Experience:
                            </Typography>
                            <Chip 
                              label={`${staff.RelevantExperience || "0"} years`}
                              size="small"
                              variant="outlined"
                              sx={{ 
                                backgroundColor: '#f0f9ff',
                                color: '#0369a1',
                                border: '1px solid #7dd3fc'
                              }}
                            />
                          </Box>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
                            <Typography variant="caption" sx={{ fontWeight: 600, color: '#374151', minWidth: '70px' }}>
                              Training:
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#1f2937' }}>
                              {staff.RelevantTraining || "N/A"}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell align="left">
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
                            <Typography variant="caption" sx={{ fontWeight: 600, color: '#374151', minWidth: '60px' }}>
                              Landline:
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#1f2937' }}>
                              {staff.LandlineNo || "N/A"}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
                            <Typography variant="caption" sx={{ fontWeight: 600, color: '#374151', minWidth: '60px' }}>
                              Mobile:
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#1f2937', fontFamily: 'monospace' }}>
                              {staff.ContactNo || "N/A"}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
                            <Typography variant="caption" sx={{ fontWeight: 600, color: '#374151', minWidth: '60px' }}>
                              Email:
                            </Typography>
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                color: '#1976d2', 
                                fontFamily: 'monospace',
                                wordBreak: 'break-all'
                              }}
                            >
                              {staff.EmailID || "N/A"}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell align="left">
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
                            <Typography variant="caption" sx={{ fontWeight: 600, color: '#374151', minWidth: '90px' }}>
                              Employee Type:
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#1f2937' }}>
                              {staff.EmployeeType || "N/A"}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
                            <Typography variant="caption" sx={{ fontWeight: 600, color: '#374151', minWidth: '90px' }}>
                              Engagement:
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#1f2937' }}>
                              {staff.typeofEngagement || "N/A"}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
                            <Typography variant="caption" sx={{ fontWeight: 600, color: '#374151', minWidth: '90px' }}>
                              Timing:
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#1f2937' }}>
                              {staff.Timming || "N/A"}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
                            <Typography variant="caption" sx={{ fontWeight: 600, color: '#374151', minWidth: '90px' }}>
                              Auth. Signatory:
                            </Typography>
                            <Chip 
                              label={staff.IsAuthorisedSignatory ? "Yes" : "No"}
                              size="small"
                              variant="filled"
                              sx={{
                                backgroundColor: staff.IsAuthorisedSignatory ? '#dcfce7' : '#fee2e2',
                                color: staff.IsAuthorisedSignatory ? '#16a34a' : '#dc2626',
                                fontWeight: 600
                              }}
                            />
                          </Box>
                          {staff.IsAuthorisedSignatory && staff.SignatorySign && (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
                              <Typography variant="caption" sx={{ fontWeight: 600, color: '#374151', minWidth: '90px' }}>
                                Signatory File:
                              </Typography>
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={(e) => {
                                  e.preventDefault();
                                  window.open(staff.SignatorySign, '_blank', 'noopener,noreferrer');
                                }}
                                sx={{
                                  fontSize: '0.75rem',
                                  px: 1,
                                  py: 0.25,
                                  minWidth: 'auto',
                                  textTransform: 'none'
                                }}
                              >
                                View File
                              </Button>
                            </Box>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell align="left" sx={{ verticalAlign: 'top' }}>
                        <Box sx={{ 
                          maxHeight: '250px', 
                          overflowY: 'auto',
                          border: '1px solid #e2e8f0',
                          borderRadius: 1,
                          backgroundColor: '#f8fafc'
                        }}>
                          {getAuthorizedAreas(staff.Id)}
                        </Box>
                      </TableCell>
                      {isObservationVisible && (
                        <TableCell align="center">
                          {renderObservationCell(getObservationBySection("Staff", staff.Id))}
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell 
                      align="center" 
                      colSpan={isObservationVisible ? 7 : 6}
                      sx={{ 
                        py: 6,
                        color: '#64748b',
                        fontStyle: 'italic'
                      }}
                    >
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          No organization staff details available
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                          Please add staff information to display here
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>
    </Box>
  );
};

export default Form73Report;
